import type { CityConfig } from "../city-template/types";

export const sanMateoConfig: CityConfig = {
  city: "San Mateo",
  state: "California",
  stateAbbr: "CA",
  slug: "san-mateo",
  route: "/cities/san-mateo",
  title: "Emergency Passport San Mateo, CA — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in San Mateo, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for the San Francisco Peninsula and Bay Area travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in San Mateo, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help San Mateo and Peninsula travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=San%20Mateo%20California%20at%20golden%20hour%20with%20downtown%20skyline%20and%20modern%20residential%20buildings%2C%20San%20Francisco%20Bay%20visible%20in%20the%20background%2C%20suburban%20Peninsula%20city%20with%20tree-lined%20streets%20and%20historic%20courthouse%2C%20warm%20sunset%20light%20over%20the%20bay%2C%20professional%20aerial%20photography%20style%2C%20clean%20modern%20Bay%20Area%20environment%2C%20no%20visible%20text%20or%20people%2C%20warm%20amber%20and%20blue%20tones&width=1800&height=700&seq=sm-hero-001&orientation=landscape",
  heroImageAlt: "San Mateo California skyline at golden hour with Bay views and Peninsula landscape",
  geoPosition: "37.5630;-122.3255",
  geoPlacename: "San Mateo, California",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St Suite 200, San Francisco, CA 94105",
    distance: "Located in San Francisco — approximately 25–35 minutes from San Mateo via US-101",
  },
  nearbyCitiesZips: [
    { name: "San Mateo", zips: "94010, 94011, 94401–94404" },
    { name: "Burlingame", zips: "94010, 94011" },
    { name: "Foster City", zips: "94404" },
    { name: "Belmont", zips: "94002" },
    { name: "San Carlos", zips: "94070" },
    { name: "Redwood City", zips: "94061–94065" },
    { name: "Menlo Park", zips: "94025, 94026" },
    { name: "Palo Alto", zips: "94301–94306" },
    { name: "Millbrae", zips: "94030" },
    { name: "South San Francisco", zips: "94080, 94083" },
    { name: "Daly City", zips: "94014, 94015" },
    { name: "Pacifica", zips: "94044, 94065" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Peninsula departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "San Francisco", route: "/cities/san-francisco" },
    { name: "San Jose", route: "/cities/san-jose" },
    { name: "Daly City", route: "/cities/daly-city" },
    { name: "Santa Clara", route: "/cities/santa-clara" },
    { name: "Berkeley", route: "/cities/berkeley" },
    { name: "Oakland", route: "/cities/oakland" },
    { name: "Sunnyvale", route: "/cities/sunnyvale" },
  ],
  fpcOfficeNote: "Fast Passport Center serves San Mateo from our local office at 400 S El Camino Real. Visit us by appointment, or use our courier pickup throughout San Mateo County and the Peninsula.",
  fpcOfficeDistance: "400 S El Camino Real — San Mateo",
  fpcOfficeAddress: "400 S El Camino Real, San Mateo, CA 94402",
  serviceAreaIntro: "From the downtown corridor to Hillsdale, from the Bayfront to the hills — Fast Passport Center serves the entire San Mateo community. We regularly help travelers from Burlingame, Foster City, Belmont, and San Carlos get their passports fast.",
  nearbyCitiesIntro: "We serve all San Mateo neighborhoods and nearby Peninsula cities including Burlingame, Foster City, Belmont, San Carlos, Redwood City, and Menlo Park. If you are in San Mateo County or the Mid-Peninsula, we have you covered.",
  howItWorksIntro: "Most San Mateo travelers complete everything online and ship documents overnight. No need to drive to San Francisco or wait in line at the regional agency.",
  mistakesIntro: "With SFO just minutes away, San Mateo travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "San Mateo and Peninsula travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in San Mateo? Here are the most common ones we hear from Peninsula travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  dmvWebsite: "https://www.dmv.ca.gov",
  dmvLabel: "California DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Peninsula international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Robert T.",
      location: "San Mateo, CA",
      rating: 5,
      text: "My business trip to London was in 2 days and my passport was expired. Fast Passport Center got me a new one in 30 hours. Saved the trip completely.",
      service: "Emergency Passport",
    },
    {
      name: "Anna S.",
      location: "Burlingame, CA",
      rating: 5,
      text: "Needed to renew for a family vacation to Italy. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Michael H.",
      location: "Foster City, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Hawaii. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Jennifer W.",
      location: "Belmont, CA",
      rating: 5,
      text: "As a frequent international traveler for work, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "sm-1",
      name: "San Mateo Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1630 S Delaware St",
      city: "San Mateo",
      state: "CA",
      zip: "94402",
      phone: "(650) 348-4413",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown San Mateo",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "sm-2",
      name: "Burlingame Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 Bayshore Hwy",
      city: "Burlingame",
      state: "CA",
      zip: "94010",
      phone: "(650) 347-1173",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.5 miles from downtown Burlingame",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "sm-3",
      name: "Redwood City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1300 Veterans Blvd",
      city: "Redwood City",
      state: "CA",
      zip: "94063",
      phone: "(650) 368-7271",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.0 miles from downtown Redwood City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular location for Peninsula residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "sm-4",
      name: "Menlo Park Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2000 E Bayshore Rd",
      city: "Menlo Park",
      state: "CA",
      zip: "94025",
      phone: "(650) 325-4617",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from downtown Menlo Park",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Peninsula residents. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "sm-5",
      name: "Palo Alto Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "390 Waverley St",
      city: "Palo Alto",
      state: "CA",
      zip: "94301",
      phone: "(650) 329-1271",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Stanford University",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Stanford and Palo Alto residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "sm-6",
      name: "San Mateo County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "555 County Center",
      city: "Redwood City",
      state: "CA",
      zip: "94063",
      phone: "(650) 363-4500",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "10 miles from San Mateo",
      website: "https://www.smcgov.org/clerk",
      websiteLabel: "San Mateo County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — San Francisco", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Peninsula photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Peninsula acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many San Mateo applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the San Francisco regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Peninsula families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "San Mateo travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in San Mateo." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your San Mateo address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in San Mateo before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in San Mateo for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Peninsula travelers." },
    { q: "How fast can I renew my passport in San Mateo?", a: "Fast Passport Center offers fast passport renewal in San Mateo in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value." },
    { q: "What if my passport expires before my Europe trip?", a: "If your passport expires before your Europe trip, contact Fast Passport Center immediately. We can expedite your passport renewal in San Mateo in 24–48 hours so you don't miss your flights." },
    { q: "Can I get a same-day passport appointment near San Mateo?", a: "Same-day passport appointments are only available at the SF Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for San Mateo residents." },
    { q: "Can I expedite a child passport in San Mateo?", a: "Yes. Fast Passport Center expedites child passports in San Mateo for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Peninsula families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in San Mateo with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in San Mateo?",
      a: "With Fast Passport Center's emergency courier service, San Mateo travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the San Francisco Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in San Mateo?",
      a: "Popular options include the San Mateo Main Post Office (94402), Burlingame Post Office (94010), Redwood City Post Office (94063), and Menlo Park Post Office (94025). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in San Mateo?",
      a: "Same-day passports are only available at the San Francisco Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live on the Peninsula or near SFO?",
      a: "We serve all of San Mateo County including Burlingame, Foster City, Belmont, San Carlos, Redwood City, Menlo Park, and Millbrae. Our overnight shipping labels work from any UPS location, so your location on the Peninsula doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in San Mateo?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in San Mateo?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in San Mateo and Burlingame offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in San Mateo?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in San Mateo?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Peninsula families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — San Mateo", address: "55 E 3rd Ave, San Mateo, CA 94401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Burlingame", address: "1450 Burlingame Ave, Burlingame, CA 94010", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Redwood City", address: "2000 Broadway, Redwood City, CA 94063", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Foster City", address: "1125 E Hillsdale Blvd, Foster City, CA 94404", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — San Carlos", address: "1919 El Camino Real, San Carlos, CA 94070", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Menlo Park", address: "1010 El Camino Real, Menlo Park, CA 94025", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Palo Alto", address: "425 University Ave, Palo Alto, CA 94301", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Redwood City", address: "1600 El Camino Real, Redwood City, CA 94063", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — San Mateo",
      address: "425 N Amphlett Blvd, San Mateo, CA 94401",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Redwood City",
      address: "300 Brewster Ave, Redwood City, CA 94063",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — San Francisco",
      address: "1377 Fell St, San Francisco, CA 94117",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Daly City",
      address: "1500 Sullivan Ave, Daly City, CA 94015",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Express services for San Mateo County residents. Appointment required.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "12 miles from San Mateo",
      address: "San Francisco, CA 94128",
      note: "The closest major international airport to San Mateo. International flights to Asia, Europe, and Latin America.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Flight Info",
      featured: true,
    },
    {
      code: "SJC",
      name: "San Jose International Airport",
      distance: "25 miles from San Mateo",
      address: "1701 Airport Blvd, San Jose, CA 95110",
      note: "International flights to Mexico, Canada, and Europe. Accessible via US-101.",
      flightUrl: "https://www.flysanjose.com/",
      flightLabel: "San Jose Airport",
      featured: false,
    },
    {
      code: "OAK",
      name: "Oakland International Airport",
      distance: "20 miles from San Mateo",
      address: "1 Airport Dr, Oakland, CA 94621",
      note: "Convenient for domestic flights and some international connections. Accessible via I-880.",
      flightUrl: "https://www.oaklandairport.com/",
      flightLabel: "Oakland Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      label: "California Dept. of Public Health (Vital Records)",
      description: "State-issued birth and death certificates for all California counties",
      url: "https://www.cdph.ca.gov/Programs/CHSI/Pages/Vital-Records.aspx",
    },
    {
      label: "San Mateo County Clerk-Recorder (Birth, Death, Marriage Records)",
      description: "Birth certificates, death certificates, and marriage records for San Mateo County",
      url: "https://www.smcgov.org/clerk",
    },
    {
      label: "San Mateo County Superior Court (Divorce Records)",
      description: "Divorce decrees and court records for San Mateo County",
      url: "https://www.sanmateo.courts.ca.gov/",
    },
  ],
};